import React, { useContext, useEffect, useRef, useState } from 'react';
import { GetDetailLinkedCompanyResponseOutDTO } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ApiContext } from '@api/api';
import useNavigation from '@hooks/useNavigation.tsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import Heading from '@components/ringil3/Heading/Heading.tsx';
import { Ringil3BadgeRow } from '@components/ringil3/styles.tsx';
import CrnBadge from '@components/ringil3/Badges/CrnBadge.tsx';
import AddressBadge from '@components/ringil3/Badges/AddressBadge.tsx';
import OriginalNameBadge from '@components/ringil3/Badges/OriginalNameBadge.tsx';
import Tabs, { TabProps } from '@components/ringil3/RingilTabs/Tabs.tsx';
import SvgInfo from '@icons/info.svg?react';
import SvgTabInternalInfo from '@components/ringil3/icons/TabInternalInfo.tsx';
import SectionPanel from '@components/framework/panels/SectionPanel.tsx';
import EntityAuditLog, { EntityAuditLogHandle } from '@components/RingilComponents/audit/EntityAuditLog.tsx';
import { AuditEntityTypeEnum } from '@api/ringil-api.v2';
import { useVisibilityAction } from '@hooks/useVisibilityAction/useVisibilityAction.tsx';
import PartnerDetailAbout from '@app/pages/settings/partner-detail/PartnerDetailAbout.tsx';

interface ComponentProps {
  partnerId: string;
}

const PartnerDetail: React.FC<ComponentProps> = ({ partnerId }) => {
  const { tr } = useTranslationLgs();

  const [activeTab, setActiveTab] = useState(0);
  const [auditVisible, setAuditVisible] = useState(false);
  const auditRef = useVisibilityAction<HTMLDivElement>(() => setAuditVisible(true), 0, true);
  const auditLogRef = useRef<EntityAuditLogHandle>(null);

  const {
    getDetailLinkedCompany,
  } = useContext(ApiContext);
  const [partnerData, setPartnerData] = useState<GetDetailLinkedCompanyResponseOutDTO>(undefined);

  const navigation = useNavigation();

  const loadPartnerData = () => {
    getDetailLinkedCompany(partnerId, data => {
      setPartnerData(data);
    });
  };

  // Reload used after a module change only. Removing the last active module unlinks the company
  // from the current user, so this reload returns 403 — in that case don't show the error, the
  // partner is simply no longer accessible, so return to the partners list. Other 403s (e.g. the
  // initial load) keep their standard error handling and are intentionally not redirected here.
  const reloadAfterModuleChange = () => {
    getDetailLinkedCompany(
      partnerId,
      data => setPartnerData(data),
      null,
      {
        onError: (error, showStandardError) => {
          if ((error as any)?.response?.status === 403) {
            navigation.navigate(navigation.urlFunctions.partnersUrlInfo('0'));
            return;
          }
          showStandardError();
        },
      },
    );
  };

  // External-id / module changes write an audit entry, so refresh the history alongside the partner data.
  const reloadWithAudit = () => {
    loadPartnerData();
    auditLogRef.current?.reload();
  };
  const reloadAfterModuleChangeWithAudit = () => {
    reloadAfterModuleChange();
    auditLogRef.current?.reload();
  };

  const load = () => {
    loadPartnerData();
  };

  useEffect(() => {
    load();
  }, []);

  const partnersBreadCrumb = (name: string) => {
    return [
      {
        label: tr(`Partners.breadCrumb`, `Partners`),
        url: navigation.createNavigationLink(navigation.urlFunctions.partnersUrlInfo('0')),
      },
      {
        label: name ? name : tr('PartnerDetail.partnerDetail', 'Partner detail'),
        disabled: true,
      },
    ];
  };

  const tabsConfig: TabProps[] = [
    {
      label: tr(`PartnerDetail.tabAbout`, `About company`),
      active: activeTab === 0,
      anchorId: 'partner-about',
      icon: <SvgInfo />,
    },
    {
      label: tr('RingilTabs.internalInfo', 'Internal info and history'),
      active: activeTab === 1,
      anchorId: 'partner-internal-info',
      icon: <SvgTabInternalInfo />,
    },
  ];

  if (!partnerData) return <></>;

  return (
    <>
      <BreadCrumbComponent items={partnersBreadCrumb(partnerData.companyInfo.companyName)} />

      <Heading
        title={partnerData.companyInfo.companyName}
        headingVariant={'h1'}
      />
      <Ringil3BadgeRow>
        <CrnBadge crn={partnerData.companyInfo.crn} />
        <AddressBadge
          country={partnerData.companyInfo.companyAddress.country}
          zipCode={partnerData.companyInfo.companyAddress.postalCode}
          city={partnerData.companyInfo.companyAddress.city}
          streetNr={partnerData.companyInfo.companyAddress.street}
          timezoneId={partnerData.companyInfo.companyAddress.timezoneId}
        />
        {partnerData.companyInfo.originalCompanyName &&
          partnerData.companyInfo.originalCompanyName !== partnerData.companyInfo.companyName && (
            <OriginalNameBadge originalName={partnerData.companyInfo.originalCompanyName} />
          )}
      </Ringil3BadgeRow>


      <Tabs
        tabs={tabsConfig}
        onTabChange={setActiveTab}
        replaceUrl
      />

      <div id={'partner-about'}>
        <PartnerDetailAbout partnerData={partnerData} reloadData={reloadWithAudit} reloadAfterModuleChange={reloadAfterModuleChangeWithAudit} />
      </div>

      <div id={'partner-internal-info'}>
        <SectionPanel icon={<SvgTabInternalInfo />} heading={tr('RingilTabs.internalInfo', 'Internal info and history')}>
          <div ref={auditRef}>{auditVisible && <EntityAuditLog ref={auditLogRef} entityType={AuditEntityTypeEnum.PARTNER} entityId={partnerData.companyInfo.companyId} />}</div>
        </SectionPanel>
      </div>
    </>
  );
};

export default PartnerDetail;
